'use client'

import { getResearchPageUrl, isArenaHost } from '@/lib/arena'
import { useI18n } from '@/lib/i18n'
import { Linkedin } from 'lucide-react'
import { Link } from 'react-router-dom'

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  )
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="m16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  )
}

const RESOURCE_LINKS = [
  { path: '/blogs', key: 'nav.blog' },
  { path: '/career', key: 'nav.career' },
  { path: '/contact-us', key: 'nav.contact' },
] as const

export function Footer() {
  const { t, language } = useI18n()
  const arena = isArenaHost()

  return (
    <footer className="bg-slate-800 text-white dark:bg-slate-900">
      <div className="container mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          { }
          <div className="text-center md:text-left">
            <h3 className="mb-4 text-2xl font-bold">Mentiss</h3>
            <div className="mb-4 text-slate-200 dark:text-slate-300">
              <p className="mt-1">{t('hero.description2')}</p>
            </div>
            <p className="text-sm text-slate-300 dark:text-slate-400">
              {t('footer.tagline')}
            </p>
          </div>

          { }
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
            { }
            <div className="text-center sm:text-left">
              <h4 className="mb-4 text-xs font-medium uppercase tracking-widest text-white sm:text-sm md:mb-6">
                Company
              </h4>
              <div className="mb-3 space-y-2 text-xs md:mb-3 md:text-sm">
                <Link to="/legal/cookies" className="block font-normal text-neutral-400 hover:text-white">
                  Cookies
                </Link>
                <Link to="/legal/privacy" className="block font-normal text-neutral-400 hover:text-white">
                  Privacy
                </Link>
                <Link to="/legal/terms" className="block font-normal text-neutral-400 hover:text-white">
                  Terms of Service
                </Link>
              </div>
            </div>

            { }
            <div className="text-center sm:text-left">
              <h4 className="mb-4 text-xs font-medium uppercase tracking-widest text-white sm:text-sm md:mb-6">
                Resources
              </h4>
              <div className="mb-3 space-y-2 text-xs md:mb-3 md:text-sm">
                {RESOURCE_LINKS.map(({ path, key }) => arena
                  ? (
                      <a
                        key={path}
                        href={getResearchPageUrl(path, language)}
                        className="block font-normal text-neutral-400 hover:text-white"
                      >
                        {t(key)}
                      </a>
                    )
                  : (
                      <Link key={path} to={path} className="block font-normal text-neutral-400 hover:text-white">
                        {t(key)}
                      </Link>
                    ))}
              </div>
            </div>

            { }
            <div className="text-center sm:text-left">
              <h4 className="mb-4 text-xs font-medium uppercase tracking-widest text-white sm:text-sm md:mb-6">
                {t('footer.followUs')}
              </h4>
              <div className="mb-3 flex flex-wrap justify-center gap-2 text-xs sm:justify-start md:mb-3 md:text-sm">
                <a
                  href="https://x.com/mentiss_ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-normal text-white hover:text-white"
                  aria-label={t('footer.followUsX')}
                >
                  <XIcon className="size-6" />
                </a>
                <a
                  href="https://discord.gg/BRE7EhVA3b"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-normal text-white hover:text-white"
                  aria-label={t('footer.joinDiscord')}
                >
                  <DiscordIcon className="size-6" />
                </a>
                <a
                  href="https://www.instagram.com/mentiss_ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-normal text-white hover:text-white"
                  aria-label={t('footer.followUsInstagram')}
                >
                  <InstagramIcon className="size-6" />
                </a>
                <a
                  href="https://www.linkedin.com/company/ai-mentiss"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-normal text-white hover:text-white"
                  aria-label={t('footer.connectLinkedIn')}
                >
                  <Linkedin className="size-6" />
                </a>
              </div>
            </div>
          </div>
        </div>

        { }
        <div className="mt-8 border-t border-slate-600 pt-8 text-center dark:border-slate-700">
          <p className="text-sm text-slate-300 dark:text-slate-400">
            © {new Date().getFullYear()}{' '}
            {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  )
}
